import { useState, useRef, useCallback, useEffect } from "react";
import { uploadTerritorialImage, importTerritorialInspectionJson, api } from "@/lib/api";
import { mapAreaPilotoFromApi } from "@/lib/mappers";

interface ImportJsonPanelProps {
  onImported?: () => void;
}

type Step = "idle" | "project" | "image" | "json" | "importing" | "done";

interface ProjectOption { id: number; name: string }
interface AreaPilotoOption { id: number; name: string; code: string; projectId: number | null }

export default function ImportJsonPanel({ onImported }: ImportJsonPanelProps) {
  const [step, setStep] = useState<Step>("idle");

  // Step 1: project/area
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [areasPiloto, setAreasPiloto] = useState<AreaPilotoOption[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedAreaPilotoId, setSelectedAreaPilotoId] = useState<number | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Step 2: image
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadedFilename, setUploadedFilename] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Step 3: json
  const [jsonText, setJsonText] = useState("");
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [resultMessage, setResultMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const imageInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step !== "project") return;
    setLoadingProjects(true);
    Promise.all([
      api.projects(),
      api.areasPiloto().then((rows) => rows.map(mapAreaPilotoFromApi)),
    ])
      .then(([projs, areas]) => {
        setProjects(projs.map((p) => ({ id: p.project_id, name: p.name })));
        setAreasPiloto(
          areas.map((a) => ({ id: a.id, name: a.name, code: a.code, projectId: a.projectId }))
        );
      })
      .catch(() => {})
      .finally(() => setLoadingProjects(false));
  }, [step]);

  const reset = useCallback(() => {
    setStep("idle");
    setSelectedProjectId(null);
    setSelectedAreaPilotoId(null);
    setImageFile(null);
    setUploadedFilename(null);
    setJsonText("");
    setJsonFile(null);
    setResultMessage("");
    setErrorMessage("");
  }, []);

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setErrorMessage("");
  }, []);

  const handleUploadImage = useCallback(async () => {
    if (!imageFile) return;
    setUploadingImage(true);
    setErrorMessage("");
    try {
      const result = await uploadTerritorialImage(imageFile);
      setUploadedFilename(result.filename);
      setStep("json");
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Erro ao enviar imagem");
    } finally {
      setUploadingImage(false);
    }
  }, [imageFile]);

  const handleJsonFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setJsonFile(file);
    const text = await file.text();
    setJsonText(text);
    setErrorMessage("");
  }, []);

  const handleImportJson = useCallback(async () => {
    setErrorMessage("");
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      setErrorMessage("JSON inválido. Verifique a formatação.");
      return;
    }

    if (uploadedFilename && parsed.inspection && typeof parsed.inspection === "object") {
      (parsed.inspection as Record<string, unknown>).image_filename = uploadedFilename;
    }

    setStep("importing");
    try {
      const result = await importTerritorialInspectionJson(parsed, {
        projectId: selectedProjectId ?? undefined,
        areaPilotoId: selectedAreaPilotoId ?? undefined,
      });
      const code = (result as Record<string, unknown>).inspection_code || "desconhecido";
      setResultMessage(`Inspeção importada com sucesso: ${code}`);
      setStep("done");
      onImported?.();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Erro ao importar JSON");
      setStep("json");
    }
  }, [jsonText, uploadedFilename, selectedProjectId, selectedAreaPilotoId, onImported]);

  const filteredAreas = selectedProjectId
    ? areasPiloto.filter((a) => a.projectId === selectedProjectId || a.projectId === null)
    : areasPiloto;

  const selectedProjectName = projects.find((p) => p.id === selectedProjectId)?.name;
  const selectedAreaName = areasPiloto.find((a) => a.id === selectedAreaPilotoId)?.name;
  const isDone = step === "done";

  // --- IDLE ---
  if (step === "idle") {
    return (
      <div className="rounded-card border border-dashed border-background-300 bg-background-50/60 p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
            <i className="ri-image-add-line text-primary-600 text-base" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground-800">Importar inspeção territorial</p>
            <p className="text-xs text-foreground-500 mt-0.5 max-w-lg">
              Selecione o projeto, faça upload da ortoimagem, analise com a LLM (FITA v1.0) e importe o JSON gerado.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setStep("project")}
          className="px-4 py-2 text-xs font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line mr-1" />
          Nova inspeção
        </button>
      </div>
    );
  }

  // --- DONE ---
  if (step === "done") {
    return (
      <div className="rounded-card border border-accent-300 bg-accent-50 p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
            <i className="ri-check-line text-accent-600 text-lg" />
          </div>
          <div>
            <p className="text-sm font-medium text-accent-800">{resultMessage}</p>
            <p className="text-xs text-accent-600 mt-0.5">A inspeção aparecerá na lista abaixo.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={reset}
          className="px-4 py-2 text-xs font-semibold text-accent-700 bg-accent-100 rounded-lg hover:bg-accent-200 transition-colors"
        >
          Nova importação
        </button>
      </div>
    );
  }

  // --- MAIN PANEL ---
  return (
    <div className="rounded-card border border-primary-200 bg-white p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground-900">Importar inspeção territorial</h3>
        <button onClick={reset} className="text-xs text-foreground-500 hover:text-foreground-700">
          <i className="ri-close-line mr-0.5" /> Cancelar
        </button>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <StepBadge
          label="1. Projeto"
          icon="ri-folder-chart-line"
          active={step === "project"}
          done={step !== "project" && selectedProjectId !== null}
        />
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <StepBadge
          label="2. Imagem"
          icon="ri-image-line"
          active={step === "image"}
          done={uploadedFilename !== null}
        />
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <StepBadge
          label="3. JSON"
          icon="ri-code-s-slash-line"
          active={step === "json" || step === "importing"}
          done={isDone}
        />
      </div>

      {/* Context bar */}
      {step !== "project" && (selectedProjectName || selectedAreaName) && (
        <div className="flex items-center gap-3 text-xs text-foreground-600 bg-background-50 border border-background-200 rounded-lg px-3 py-2 flex-wrap">
          {selectedProjectName && (
            <span className="flex items-center gap-1">
              <i className="ri-folder-chart-line text-primary-500" />
              {selectedProjectName}
            </span>
          )}
          {selectedAreaName && (
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line text-primary-500" />
              {selectedAreaName}
            </span>
          )}
          {step !== "importing" && (
            <button onClick={() => setStep("project")} className="ml-auto text-primary-600 hover:text-primary-700 underline">
              Alterar
            </button>
          )}
        </div>
      )}

      {/* Step 1: Project/Area */}
      {step === "project" && (
        <div className="space-y-4">
          <p className="text-xs text-foreground-600">
            Selecione o projeto e a área de estudo para vincular à inspeção.
          </p>

          {loadingProjects ? (
            <p className="text-xs text-foreground-400">Carregando projetos...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Projeto</label>
                <select
                  value={selectedProjectId ?? ""}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : null;
                    setSelectedProjectId(val);
                    setSelectedAreaPilotoId(null);
                  }}
                  className="w-full text-sm border border-background-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  <option value="">— Selecione um projeto —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Área de estudo</label>
                <select
                  value={selectedAreaPilotoId ?? ""}
                  onChange={(e) => setSelectedAreaPilotoId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full text-sm border border-background-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                >
                  <option value="">— Selecione (opcional) —</option>
                  {filteredAreas.map((a) => (
                    <option key={a.id} value={a.id}>{a.name} ({a.code})</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={() => setStep("image")}
              disabled={!selectedProjectId}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60"
            >
              Continuar <i className="ri-arrow-right-s-line ml-0.5" />
            </button>
            <button
              onClick={() => { setSelectedProjectId(null); setSelectedAreaPilotoId(null); setStep("image"); }}
              className="text-xs text-foreground-500 hover:text-foreground-700"
            >
              Pular — sem vincular projeto
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Image */}
      {step === "image" && (
        <div className="space-y-3">
          <p className="text-xs text-foreground-600">
            Selecione a ortoimagem da quadra. Ela será salva no servidor e vinculada à inspeção.
          </p>
          <div className="flex items-center gap-3">
            <input
              ref={imageInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.tif,.tiff,.webp"
              onChange={handleImageSelect}
              className="hidden"
            />
            <button
              onClick={() => imageInputRef.current?.click()}
              className="px-3 py-2 text-xs font-semibold text-foreground-700 bg-background-50 border border-background-300 rounded-lg hover:bg-background-100 transition-colors"
            >
              <i className="ri-folder-open-line mr-1" />
              Selecionar imagem
            </button>
            {imageFile && (
              <span className="text-xs text-foreground-600 truncate max-w-xs">
                {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(1)} MB)
              </span>
            )}
          </div>

          {imageFile && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleUploadImage}
                disabled={uploadingImage}
                className="px-4 py-2 text-xs font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {uploadingImage ? (
                  <><i className="ri-loader-4-line animate-spin mr-1" /> Enviando...</>
                ) : (
                  <><i className="ri-upload-2-line mr-1" /> Enviar imagem</>
                )}
              </button>
              <button
                onClick={() => setStep("json")}
                className="px-3 py-2 text-xs font-medium text-foreground-500 hover:text-foreground-700"
              >
                Pular (sem imagem)
              </button>
            </div>
          )}

          {!imageFile && (
            <button
              onClick={() => setStep("json")}
              className="text-xs text-foreground-500 hover:text-foreground-700 underline"
            >
              Pular — importar apenas o JSON sem imagem
            </button>
          )}
        </div>
      )}

      {/* Step 3: JSON */}
      {(step === "json" || step === "importing") && (
        <div className="space-y-3">
          {uploadedFilename && (
            <div className="flex items-center gap-2 text-xs text-accent-700 bg-accent-50 border border-accent-200 rounded-lg px-3 py-2">
              <i className="ri-check-line" />
              Imagem enviada: <code className="font-mono bg-accent-100 px-1 rounded">{uploadedFilename}</code>
            </div>
          )}

          <p className="text-xs text-foreground-600">
            Cole o JSON gerado pela LLM (FITA v1.0 / CDIT v1.1) ou selecione o arquivo <code className="font-mono text-[11px] bg-background-100 px-1 rounded">02_Inspecao.json</code>.
          </p>

          <div className="flex items-center gap-3">
            <input
              ref={jsonInputRef}
              type="file"
              accept=".json"
              onChange={handleJsonFileSelect}
              className="hidden"
            />
            <button
              onClick={() => jsonInputRef.current?.click()}
              className="px-3 py-2 text-xs font-semibold text-foreground-700 bg-background-50 border border-background-300 rounded-lg hover:bg-background-100 transition-colors"
            >
              <i className="ri-file-code-line mr-1" />
              Selecionar JSON
            </button>
            {jsonFile && (
              <span className="text-xs text-foreground-600 truncate max-w-xs">{jsonFile.name}</span>
            )}
          </div>

          <textarea
            value={jsonText}
            onChange={(e) => { setJsonText(e.target.value); setErrorMessage(""); }}
            placeholder='{"inspection": { ... }, "roads": [...], ...}'
            rows={10}
            className="w-full text-xs font-mono bg-background-50 border border-background-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200 resize-y"
          />

          <div className="flex items-center gap-3">
            <button
              onClick={handleImportJson}
              disabled={!jsonText.trim() || step === "importing"}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60"
            >
              {step === "importing" ? (
                <><i className="ri-loader-4-line animate-spin mr-1" /> Importando...</>
              ) : (
                <><i className="ri-database-2-line mr-1" /> Importar no banco</>
              )}
            </button>
            {step !== "importing" && (
              <button
                onClick={() => setStep("image")}
                className="text-xs text-foreground-500 hover:text-foreground-700"
              >
                <i className="ri-arrow-left-s-line" /> Voltar
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {errorMessage && (
        <div className="flex items-start gap-2 text-xs text-danger-700 bg-danger-50 border border-danger-200 rounded-lg px-3 py-2">
          <i className="ri-error-warning-line shrink-0 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
}

function StepBadge({ label, icon, active, done }: { label: string; icon: string; active: boolean; done: boolean }) {
  const cls = active
    ? "bg-primary-100 text-primary-700"
    : done
      ? "bg-accent-100 text-accent-700"
      : "bg-background-100 text-foreground-400";
  return (
    <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium ${cls}`}>
      <i className={done ? "ri-check-line" : icon} />
      {label}
    </span>
  );
}
